// Engagement Intelligence Console (core-lambda admin_module/engagement_console).
// Visibility into every outbound message (push/email/sms/in-app), delivery &
// open/click, and which messages drove sessions/conversions — down to a single
// user's full message history. See the frontend integration contract.
//
// All dashboard endpoints share a time window: either a `range` preset string
// (today|yesterday|7d|30d|60d|90d|ytd|lifetime) OR an explicit {start,end} ISO
// pair (start+end overrides range). The two /users endpoints take no range.

import { axiosAssessmentInstance } from "../AxiosConfiguration";

const unwrap = (response, fallback = null) =>
  response?.data?.payload ?? fallback;

// The admin's IANA timezone, used for day-aligned ranges (today/yesterday/ytd/
// custom) and Send-Time bucketing on the global dashboards (§3.5). Invalid/
// unknown zones fall back to UTC server-side.
export const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

// `range` is either a preset string or a {start,end} custom range. Build the
// matching query params (+ tz); custom range wins. Pass tz=null to omit it.
const rangeParams = (range, extra = {}, tz = browserTimezone()) => {
  const params = { ...extra };
  if (tz) params.tz = tz;
  if (range && typeof range === "object" && range.start && range.end) {
    params.start = range.start;
    params.end = range.end;
  } else if (range) {
    params.range = range;
  }
  return params;
};

/** Dashboard 1 — Executive Overview (volume, rates, conversions, daily trend). */
export const getEngagementOverview = async (range = "30d") => {
  const response = await axiosAssessmentInstance.get(
    "/api/admin/engagement/overview",
    { params: rangeParams(range) }
  );
  return unwrap(response, {});
};

/**
 * Dashboard 2 — Message Performance.
 * @param {string|{start,end}} range
 * @param {{rank?: "best"|"worst"|null, limit?: number}} opts
 */
export const getEngagementMessages = async (
  range = "30d",
  { rank = null, limit = 50 } = {}
) => {
  const extra = { limit };
  if (rank) extra.rank = rank;
  const response = await axiosAssessmentInstance.get(
    "/api/admin/engagement/messages",
    { params: rangeParams(range, extra) }
  );
  return unwrap(response, {});
};

/** Dashboard 3 — Channel Performance comparison. */
export const getEngagementChannels = async (range = "30d") => {
  const response = await axiosAssessmentInstance.get(
    "/api/admin/engagement/channels",
    { params: rangeParams(range) }
  );
  return unwrap(response, {});
};

/** Dashboard 4 — Send-Time Intelligence (by hour 0..23 UTC, by day-of-week). */
export const getEngagementSendTime = async (range = "30d") => {
  const response = await axiosAssessmentInstance.get(
    "/api/admin/engagement/send-time",
    { params: rangeParams(range) }
  );
  return unwrap(response, {});
};

/** Dashboard 5 — User Segment Performance (by engagement state, by life pillar). */
export const getEngagementSegments = async (range = "30d") => {
  const response = await axiosAssessmentInstance.get(
    "/api/admin/engagement/segments",
    { params: rangeParams(range) }
  );
  return unwrap(response, {});
};

/** Dashboard 6 — User Journey / Funnel (one funnel per message category). */
export const getEngagementFunnel = async (range = "30d") => {
  const response = await axiosAssessmentInstance.get(
    "/api/admin/engagement/funnel",
    { params: rangeParams(range) }
  );
  return unwrap(response, {});
};

/**
 * Dashboard 7a — User search (by name / email / userId). `q` must be non-empty.
 * @param {string} q
 * @param {number} limit
 */
export const searchEngagementUsers = async (q, limit = 20) => {
  const response = await axiosAssessmentInstance.get(
    "/api/admin/engagement/users/search",
    { params: { q, limit } }
  );
  return unwrap(response, { users: [] });
};

/**
 * Dashboard 7b — Individual user profile + paginated message history. This is
 * the screen that shows the exact message text delivered to a user.
 * @param {string} userId
 * @param {{page?: number, pageSize?: number}} opts page_size max 200
 */
export const getEngagementUser = async (
  userId,
  { page = 1, pageSize = 50 } = {}
) => {
  const response = await axiosAssessmentInstance.get(
    `/api/admin/engagement/users/${userId}`,
    { params: { page, page_size: pageSize } }
  );
  return unwrap(response, {});
};

/** Dashboard 8 — Conversion Attribution (last-touch, 24h heuristic). */
export const getEngagementAttribution = async (range = "30d") => {
  const response = await axiosAssessmentInstance.get(
    "/api/admin/engagement/attribution",
    { params: rangeParams(range) }
  );
  return unwrap(response, {});
};
